import React, {useEffect, useState} from 'react'
import {graphql, navigate} from 'gatsby'
import pxToRem from '../utils/pxToRem'
import Layout from '../components/Layout/Layout'
import Hero, {HeroVariantEnum} from '../components/Hero/Hero'
import theme from '../theme/theme-style'
import ShareIcon from '../images/icons/share.svg'
import ContactIcon from '../images/icons/contact.svg'
import PrintIcon from '../images/icons/print.svg'
import ShareOverlay from '../components/ShareOverlay/ShareOverlay'
import ContentType from '../enums/ContentType'
import {sendEmailWithLink} from '../utils/SendEmail'
import printPage from '../utils/PrintPage'
import ButtonVariantEnum from '../enums/ButtonVariant'
import {formatDate} from '../utils/FormatDate'
import HeadingSizeEnum from '../enums/headingSize.js'
import BackIcon from '../images/icons/clients-arrow-left.svg'
import FilaireIcon from '../images/icons/filaire.svg'
import GlobeIcon from '../images/icons/globe.svg'
import CalendarIcon from '../images/icons/calendar.svg'
import NextIcon from '../images/icons/clients-arrow-right.svg'
import styled, {css} from 'styled-components'
import {deviceMax} from '../theme/breakpoints'
import IconButton from '../components/IconButton/IconButton'
import Button from '../components/Button/Button'
import Heading from '../components/Heading/Heading'
import Seo from '../components/Seo/Seo'
import {slugify} from '../utils/UrlFactory'
import {TYPES_ENUM} from '../config/BreadCrumbsConfig'
import {MainContainer} from '../theme/mainContainer.styled'
import RoutingEnum from '../routes/appRoutes'
import JobSearch from '../pages/careers/job-search'
import {applyJobEvent, mostViewedJobEvent} from '../utils/gtmEvents'
import parse from 'html-react-parser'
import useMediaQuery from '../hooks/useMediaQuery'

const backIcon = <BackIcon/>
const filaireIcon = <FilaireIcon/>
const globeIcon = <GlobeIcon/>
const calendarIcon = <CalendarIcon/>
const nextIcon = <NextIcon/>
const APPLY = 'Apply'

const STYLED_ICONS = css`
  margin-right: ${pxToRem(10)};

  path {
    fill: ${({theme}) => theme.colors.primary.blue};
  }
`

const STYLED_CONTENT = css`
  font-family: ${({theme}) => theme.fonts.family.sans} !important;
  font-size: ${({theme}) => theme.fonts.sizes.base} !important;
  line-height: ${({theme}) => theme.fonts.lineHeight.button} !important;
`

const StyledContainer = styled(MainContainer)`
  background-color: ${({theme}) => theme.colors.unique.white};
  padding: ${pxToRem(25)} ${pxToRem(20)} ${pxToRem(20)};
  border-radius: ${pxToRem(5)};
`

const StyledActions = styled.div`
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  display: flex;
  margin-bottom: ${pxToRem(30)};
  @media ${deviceMax.tablet} {
    flex-wrap: wrap;
  }
`

const StyledBackLink = styled.button`
  align-items: center;
  padding: initial;
  border: none;
  display: flex;
  height: inherit;
  background-color: initial;
  font-size: ${({theme}) => theme.fonts.sizes.button};
  margin-bottom: ${pxToRem(10)};
  cursor: pointer;
`

const StyledBackIcon = styled.div`
  align-items: center;
  display: flex;
`

const StyledIconsContainer = styled.div`
  justify-content: space-between;
  align-items: center;
  display: flex;
  @media ${deviceMax.tablet} {
    flex-wrap: wrap;
    justify-content: center;
  }
`

const StyledIcons = styled.div`
  flex-direction: row;
  align-items: center;
  display: flex;
  @media ${deviceMax.mobileM} {
    margin: ${pxToRem(10)};
  }
`

const StyledButtonIcon = styled(IconButton)`
  justify-content: center;
  align-items: center;
  display: flex;
  border: ${pxToRem(1)} solid ${({theme}) => theme.colors.unique.black};
  border-radius: ${pxToRem(5)};
  padding: ${pxToRem(3)};
  width: ${pxToRem(30)};
  height: ${pxToRem(30)};
  margin-left: ${pxToRem(20)};

  & svg {
    height: ${pxToRem(24)};
  }
`

const StyledShareIcon = styled(StyledButtonIcon)`
  & svg {
    padding: 0;
  }
`
const StyleddButton = styled(Button)`
  margin-left: ${pxToRem(30)};
  padding: ${pxToRem(10.5)} ${pxToRem(55)};
  font-weight: ${({theme}) => theme.fonts.weight.normal};

  @media ${deviceMax.mobileL} {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-left: ${pxToRem(0)};
    margin-bottom: ${pxToRem(30)};
    font-weight: ${({theme}) => theme.fonts.weight.semibold};
  }
`

const StyledHeading = styled(Heading)`
  margin-bottom: ${pxToRem(20)};
`

const StyledJobIdentity = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: start;
  align-items: center;
  margin-bottom: ${pxToRem(50)};
`

const StyledChip = styled.div`
  margin-right: ${pxToRem(25)};
  padding: ${pxToRem(5)} ${pxToRem(20)};
  border: ${pxToRem(1)} solid ${({theme}) => theme.colors.unique.black};
  border-radius: ${pxToRem(40)};
  font-size: ${({theme}) => theme.fonts.sizes.md};
  line-height: ${({theme}) => theme.fonts.lineHeight.md};
`

const StyledId = styled.p`
  font-size: ${({theme}) => theme.fonts.sizes.base};
  line-height: ${({theme}) => theme.fonts.lineHeight.base};
  font-weight: ${({theme}) => theme.fonts.weight.semibold};
`

const StyledJobInfos = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-around;
  margin: ${pxToRem(10)} 0;
  @media ${deviceMax.tablet} {
    flex-direction: column;
  }
`

const StyledRow = styled.div`
  width: 50%;
  flex-direction: row;
  display: flex;
  justify-content: space-around;
  align-items: center;
  @media ${deviceMax.tablet} {
    width: 100%;
    justify-content: space-between;
  }
`

const StyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  :last-child {
    margin-right: ${pxToRem(20)};
  }

  @media ${deviceMax.tablet} {
    width: 50%;
    :last-child {
      justify-content: end;
      margin-right: 0;
    }
  }

  @media ${deviceMax.mobileL} {
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
`
const StyledInfosDiv = styled.div`
  display: flex;
  flex-direction: column;
  vertical-align: middle;
`

const StyledFilaireIcon = styled(FilaireIcon)`
  ${STYLED_ICONS};
`
const StyledGlobeIcon = styled(GlobeIcon)`
  ${STYLED_ICONS};
`
const StyledCalendarIcon = styled(CalendarIcon)`
  ${STYLED_ICONS};
`
const StyledNextIcon = styled(NextIcon)`
  ${STYLED_ICONS};
`
const StyledBold = styled.p`
  font-weight: ${({theme}) => theme.fonts.weight.semibold};
`

const StyledContent = styled.div`
  p,
  span,
  ul,
  li,
  table {
    ${STYLED_CONTENT};
  }

  b,
  strong {
    font-weight: ${({theme}) => theme.fonts.weight.semibold};
  }

  table {
    margin: auto;
  }

  ul li::before,
  ol li::before {
    content: '\\2022'; /*\\2022 is the CSS Code/unicode for a bullet */
    color: ${({theme}) => theme.colors.primary.blue};
    font-weight: ${({theme}) => theme.fonts.weight.semibold};
    display: inline-block;
    width: 1rem;
  }
`

const StyledSeparator = styled.div`
  height: ${pxToRem(40)};
  width: ${pxToRem(1)};
  background-color: ${({theme}) => theme.colors.secondary.grey95};
`

const StyledSeparatorSide = styled.div`
  margin-bottom: ${({$bottom}) => ($bottom ? pxToRem(30) : 0)};
  height: ${pxToRem(1)};
  background-color: ${({theme}) => theme.colors.secondary.grey95};
`

const MULTIPLE_LOCATIONS = 'Multiple locations'
const MOBILE_TITLE = 'Job offer'
const REGEX_DATE = '\\d+'

const JobPage = ({data, location}) => {
  if (data?.jobs === null) {
    return <JobSearch/>
  }

  const [isSharing, setIsSharing] = useState(false)

  const {
    category,
    description,
    id,
    isInternship,
    locations: jobLocations,
    publicationDate,
    remotePosition,
    title,
    header,
    footer,
  } = data?.jobs

  const infosLocations = jobLocations?.map((jobLocation) => {
    const {country, state, location} = jobLocation
    return <StyledBold>{`${country}, ${state}, ${location}`}</StyledBold>
  })
  const multipleLocation = <StyledBold>{MULTIPLE_LOCATIONS}</StyledBold>

  const date = formatDate(Number(publicationDate))
  const isInternshipToString = isInternship ? 'Yes' : 'No'
  const isRemote = remotePosition ? 'Yes' : 'No'
  const sluggedTitle = slugify(title)
  const newPath = RoutingEnum.jobSearch + '/' + sluggedTitle

  // TODO: update when multiloc available
  const metaTitle = `Job offer: ${title}`
  let metaDescLocation
  let metaDescLocations = []
  if (jobLocations && jobLocations.length > 0) {
    for (let metaLocation of jobLocations) {
      metaDescLocations.push(metaLocation.location)
    }
    metaDescLocation = metaDescLocations.join()
  } else {
    metaDescLocation = ''
  }

  const metaDescription = `Apply now to our ${title} offer in ${metaDescLocation} | Constellium`

  const isMobile = useMediaQuery(deviceMax.mobileL)
  const isTablet = useMediaQuery(deviceMax.tablet)

  const gtmEventPayload = {
    pageCategory: 'job offers',
    title,
    id,
  }
  // location: `${jobLocations[0].country}, ${infosLocation}`

  useEffect(() => {
    mostViewedJobEvent(gtmEventPayload)
  }, [])

  const ToggleSendEmail = () => sendEmailWithLink()
  const TogglePrint = () => printPage()
  const ToggleShareOverlay = () => setIsSharing(!isSharing)

  const buttonApply = (
    <StyleddButton
      label={APPLY}
      variant={ButtonVariantEnum.primary}
      onClick={() => applyJobEvent(gtmEventPayload)}
      href={`https://career5.successfactors.eu/career?company=C0007086225P&site=&career_ns=job_application&login_ns=register&lang=en_US&career_job_req_id=${id}`}
      target={'_blank'}
    />
  )

  const handleBackToList = () => {
    if (history.length > 2) {
      return navigate(-1)
    }
    navigate(RoutingEnum.jobSearch)
  }
  return (
    <Layout>
      <Seo
        title={metaTitle}
        description={metaDescription}
        canonicalHref={location.pathname}
      />
      <Hero
        pathname={newPath}
        slug={newPath}
        type={TYPES_ENUM.job}
        title={isMobile ? MOBILE_TITLE : title}
        variant={HeroVariantEnum.regular}
      />
      {isSharing && (
        <ShareOverlay
          title={isMobile ? MOBILE_TITLE : title}
          setIsSharing={setIsSharing}
          contentType={ContentType.job}
        />
      )}
      <StyledContainer>
        <StyledActions>
          <StyledBackLink onClick={handleBackToList}>
            <StyledBackIcon>{backIcon}</StyledBackIcon>
            {'Back to list'}
          </StyledBackLink>
          <StyledIconsContainer>
            <StyledIcons>
              <StyledShareIcon
                color={theme.colors.unique.transparent}
                width={pxToRem(24)}
                height={pxToRem(24)}
                onClick={ToggleShareOverlay}
              >
                <ShareIcon/>
              </StyledShareIcon>
              <StyledButtonIcon
                color={theme.colors.unique.black}
                width={pxToRem(24)}
                height={pxToRem(24)}
                onClick={ToggleSendEmail}
              >
                <ContactIcon/>
              </StyledButtonIcon>
              <StyledButtonIcon
                color={theme.colors.unique.black}
                width={pxToRem(24)}
                height={pxToRem(24)}
                onClick={TogglePrint}
              >
                <PrintIcon/>
              </StyledButtonIcon>
            </StyledIcons>

            {!isMobile && buttonApply}
          </StyledIconsContainer>
        </StyledActions>

        <StyledJobIdentity>
          <StyledChip>{category}</StyledChip>
          <StyledId>{id}</StyledId>
        </StyledJobIdentity>
        {isMobile && buttonApply}
        {isMobile && <StyledHeading label={title} size={HeadingSizeEnum.h2}/>}
        <StyledSeparatorSide $bottom={false}/>
        <StyledJobInfos>
          <StyledRow>
            <StyledDiv>
              <StyledFilaireIcon>{filaireIcon}</StyledFilaireIcon>
              <StyledInfosDiv>
                {jobLocations.length > 1 ? multipleLocation : infosLocations}
              </StyledInfosDiv>
            </StyledDiv>
            <StyledSeparator/>
            <StyledDiv>
              <StyledGlobeIcon>{globeIcon}</StyledGlobeIcon>
              <p>{'Remote Position:'}&nbsp;</p>
              <StyledBold>{isRemote}</StyledBold>
            </StyledDiv>
          </StyledRow>
          <StyledRow>
            {!isMobile && !isTablet && <StyledSeparator/>}
            <StyledDiv>
              <StyledCalendarIcon>{calendarIcon}</StyledCalendarIcon>
              <StyledBold>{date}</StyledBold>
            </StyledDiv>
            <StyledSeparator/>
            <StyledDiv>
              <StyledNextIcon>{nextIcon}</StyledNextIcon>
              <p>{'Internship:'}&nbsp;</p>
              <StyledBold>{isInternshipToString}</StyledBold>
            </StyledDiv>
          </StyledRow>
        </StyledJobInfos>
        <StyledSeparatorSide $bottom={true}/>
        {header && <StyledContent>{parse(header)}</StyledContent>}
        <StyledContent>{parse(description)}</StyledContent>
        {footer && <StyledContent>{parse(footer)}</StyledContent>}
      </StyledContainer>
    </Layout>
  )
}

export default JobPage

export const job = graphql`
  query jobQuery($id: String!) {
    jobs(id: { eq: $id }) {
      category
      description
      id
      isInternship
      locations {
        country
        state
        location
      }
      publicationDate
      remotePosition
      title
      header
      footer
    }
  }
`
